import UploadIcons from '@/icons/UploadIcons'
import {
    getContactImportTemplate,
    importContactsFromExcel,
    ImportContactResult,
} from '@/modules/Marketing/services'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { IPermissionProps } from '@/types/shared'
import { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Modal } from 'vnetwork-ui'

const ImportContactButton: React.FC<IPermissionProps> = () => {
    const { t } = useTranslation()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)
    const [showResultModal, setShowResultModal] = useState(false)
    const [importResult, setImportResult] =
        useState<ImportContactResult | null>(null)
    const { refetchCurrent } = useDataGridRefetch({
        reactQueryKey: 'contact-source-list',
    })

    const handleDownloadTemplate = async () => {
        setIsDownloading(true)
        try {
            const response = await getContactImportTemplate()
            const url = window.URL.createObjectURL(new Blob([response]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'contact_import_template.xlsx')
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
            toast.success(t('dowload-success'))
        } catch (error) {
            console.error('Error downloading template:', error)
            toast.error(t('download-failed'))
        } finally {
            setIsDownloading(false)
        }
    }

    const handleFileSelect = () => {
        fileInputRef.current?.click()
    }

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        // Validate file type
        const validTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
        ]
        if (!validTypes.includes(file.type)) {
            toast.error(t('import-invalid-file-type'))
            return
        }

        setIsUploading(true)
        try {
            const response = await importContactsFromExcel(file)
            setImportResult(response.result)
            setShowResultModal(true)
            refetchCurrent()

            if (response.result.error_count === 0) {
                toast.success(
                    `${t('import-success')}: ${response.result.success_count} contacts`
                )
            } else {
                toast.warning(
                    `${t('import-partial-success')}: ${response.result.success_count} ${t('success')}, ${response.result.error_count} ${t('errors')}`
                )
            }
        } catch (error: any) {
            console.error('Error importing contacts:', error)
            toast.error(error?.response?.data?.message || t('import-failed'))
        } finally {
            setIsUploading(false)
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
            />
            <div className="flex gap-2" data-tour="import-btn">
                <Button
                    size="small"
                    variant="text"
                    onClick={handleDownloadTemplate}
                    loading={isDownloading}
                >
                    {t('download-template')}
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadIcons />}
                    onClick={handleFileSelect}
                    loading={isUploading}
                >
                    {t('import')}
                </Button>
            </div>

            <Modal
                open={showResultModal}
                onClose={() => setShowResultModal(false)}
                title={t('import-result')}
            >
                <div className="p-4 space-y-4">
                    <div className="flex gap-4">
                        <div className="flex-1 p-3 bg-green-50 rounded-lg">
                            <p className="text-sm text-green-600">
                                {t('success')}
                            </p>
                            <p className="text-2xl font-bold text-green-700">
                                {importResult?.success_count || 0}
                            </p>
                        </div>
                        <div className="flex-1 p-3 bg-red-50 rounded-lg">
                            <p className="text-sm text-red-600">
                                {t('errors')}
                            </p>
                            <p className="text-2xl font-bold text-red-700">
                                {importResult?.error_count || 0}
                            </p>
                        </div>
                    </div>

                    {importResult?.errors && importResult.errors.length > 0 && (
                        <div className="mt-4">
                            <p className="font-medium text-gray-700 mb-2">
                                {t('error-details')}:
                            </p>
                            <div className="max-h-48 overflow-y-auto border rounded-lg">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                            <th className="px-3 py-2 text-left">
                                                {t('row')}
                                            </th>
                                            <th className="px-3 py-2 text-left">
                                                Email
                                            </th>
                                            <th className="px-3 py-2 text-left">
                                                {t('message')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {importResult.errors.map(
                                            (error, idx) => (
                                                <tr
                                                    key={idx}
                                                    className="border-t hover:bg-gray-50"
                                                >
                                                    <td className="px-3 py-2">
                                                        {error.row}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        {error.email}
                                                    </td>
                                                    <td className="px-3 py-2 text-red-600">
                                                        {error.message}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end pt-2">
                        <Button
                            variant="contained"
                            onClick={() => setShowResultModal(false)}
                        >
                            {t('close')}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    )
}

export default ImportContactButton
